import { useQuery } from '@tanstack/react-query';
import { api, type InboxQuery } from './api';
import type { InboxFilter } from './types';

export const qk = {
  me: ['me'] as const,
  wa: ['wa-status'] as const,
  inbox: (filter: InboxFilter, search: string) => ['inbox', filter, search] as const,
  conversation: (id: string) => ['conversation', id] as const,
  tickets: (status: string) => ['tickets', status] as const,
  ticket: (id: string) => ['ticket', id] as const,
  faqs: ['faqs'] as const,
  docs: ['docs'] as const,
  ai: ['ai-settings'] as const,
  testMode: ['test-mode'] as const,
  audit: (action: string, result: string, search: string) => ['audit', action, result, search] as const,
  settings: ['settings'] as const,
  contact: (id: string) => ['contact', id] as const
};

export function useWaStatus() {
  return useQuery({
    queryKey: qk.wa,
    queryFn: api.waStatus,
    staleTime: 30_000,
    retry: false,
    refetchInterval: (query) => (query.state.data?.unavailable ? false : 30_000)
  });
}

export function useInbox(query: InboxQuery) {
  return useQuery({
    queryKey: [...qk.inbox(query.filter, query.search), query.userId ?? ''] as const,
    queryFn: () => api.inbox(query),
    staleTime: 5_000
  });
}

/** WA bağlantı durumu; eşleştirme sırasında 3 sn'de bir tazelenir. */
export function useWaConnection() {
  return useQuery({
    queryKey: qk.wa,
    queryFn: api.waStatus,
    retry: false,
    // Uç yoksa (404) boşuna tekrar denemeyiz; eşleştirmede 3 sn, normalde 15 sn.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data || data.unavailable) return false;
      // Bağlantı ekranı: eşleştirme sırasında 5 sn poll (task-22).
      return data.status === 'pairing' || data.status === 'connecting' ? 5_000 : 15_000;
    }
  });
}

export function useConversation(id: string | undefined) {
  return useQuery({
    queryKey: qk.conversation(id || ''),
    queryFn: () => api.conversation(id as string),
    enabled: Boolean(id),
    staleTime: 3_000
  });
}

export function useTickets(status: string, enabled = true) {
  return useQuery({ queryKey: qk.tickets(status), queryFn: () => api.tickets(status), staleTime: 5_000, enabled });
}

export function useTicket(id: string | undefined) {
  return useQuery({
    queryKey: qk.ticket(id || ''),
    queryFn: () => api.ticket(id as string),
    enabled: Boolean(id),
    staleTime: 5_000
  });
}

export function useFaqs() {
  return useQuery({ queryKey: qk.faqs, queryFn: api.faqs, staleTime: 10_000 });
}

export function useDocs() {
  return useQuery({
    queryKey: qk.docs,
    queryFn: api.docs,
    // İşleme durumu canlı görünsün diye kısa aralıkla tazelenir.
    refetchInterval: (query) =>
      query.state.data?.items.some((doc) => doc.status === 'processing' || doc.status === 'queued') ? 2_000 : false
  });
}

export function useAiSettings() {
  return useQuery({ queryKey: qk.ai, queryFn: api.aiSettings, staleTime: 30_000 });
}

export function useTestMode() {
  return useQuery({ queryKey: qk.testMode, queryFn: api.testMode, staleTime: 10_000 });
}

export function useAudit(query: { action: string; actor: string }) {
  return useQuery({
    queryKey: ['audit', query.action, query.actor] as const,
    queryFn: () => api.audit({ action: query.action || undefined, actor: query.actor || undefined }),
    staleTime: 10_000
  });
}

export function useSettings() {
  return useQuery({ queryKey: qk.settings, queryFn: api.settings, staleTime: 30_000, retry: false });
}

export function useContacts(search: string, enabled = true) {
  return useQuery({ queryKey: ['contacts', search] as const, queryFn: () => api.contacts(search), staleTime: 10_000, enabled });
}

export function useContact(id: string | undefined) {
  return useQuery({
    queryKey: qk.contact(id || ''),
    queryFn: () => api.contact(id as string),
    enabled: Boolean(id),
    staleTime: 10_000
  });
}